<template>
  <v-container>
    <v-data-table
      fixed-header
      :header-props="{ sortIcon: 'fas fa-sort-amount-up-alt' }"
      :headers="headers"
      :items="items"
      :loading="loading"
      sort-by="updated_at"
      :sort-desc="true"
      :items-per-page="itemsPerPage"
      @update:items-per-page="updateRecordsPerPage"
      :footer-props="{
        itemsPerPageOptions: [10, 25, 50, 100],
        showFirstLastPage: true
      }"
      :options.sync="options"
      :server-items-length="totalItems"
      :dense="showDense"
      class="elevation-2"
    >
      <template v-slot:top>
        <v-row align="center" class="py-1 px-2" no-gutters>
          <v-col cols="12" sm="6">
            <v-breadcrumbs
              class="px-2"
              :items="breadcrumbsItems"
            ></v-breadcrumbs>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            :class="
              $store.getters.LANGUAGE == 'fa' ? 'text-left' : 'text-right'
            "
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  color="primary"
                  :to="{ name: 'Upload' }"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>fa-plus</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("new") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  @click.stop="getBatches"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>fa-sync</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("refresh") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  @click="showDense = !showDense"
                  v-bind="attrs"
                  v-on="on"
                  :color="showDense ? 'success' : ''"
                >
                  <v-icon small>fas fa-compress-alt</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("compactView") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  class="mx-2"
                  v-bind="attrs"
                  v-on="on"
                  color="orange"
                  @click="helpDialog = true"
                >
                  <v-icon small>fa-question</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.translator("showHelp") }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </template>
      <template v-slot:item.index="{ item }">
        {{
          (options.page - 1) * options.itemsPerPage + items.indexOf(item) + 1
        }}
      </template>
      <template v-slot:item.files="{ item }">
        {{ item.images_count }} {{ $vuetify.lang.translator("of") }}
        {{ item.files }}
      </template>
      <template v-slot:item.updated_at="{ item }">
        <div class="ltr-text" v-if="$store.getters.LANGUAGE == 'fa'">
          {{ item.updated_at | moment("jYYYY/jM/jD HH:mm") }}
        </div>
        <div class="ltr-text" v-else>
          {{ item.updated_at | moment("YYYY/M/D HH:mm") }}
        </div>
      </template>
      <template v-slot:item.captured_at="{ item }">
        <div class="ltr-text" v-if="$store.getters.LANGUAGE == 'fa'">
          {{ item.captured_at | moment("jYYYY/jM/jD HH:mm") }}
        </div>
        <div class="ltr-text" v-else>
          {{ item.captured_at | moment("YYYY/M/D HH:mm") }}
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              :to="{
                name: 'Images',
                params: { id: item.id }
              }"
              v-bind="attrs"
              v-on="on"
              class="mx-1"
            >
              <v-icon small>fa-eye</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("viewImages") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              :to="{
                name: 'ContinueUpload',
                params: { id: item.id }
              }"
              v-bind="attrs"
              v-on="on"
              v-if="item.files != item.images_count"
              class="mx-1"
            >
              <v-icon small>fa-upload</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("continueUploadingImages") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              :to="{
                name: 'BatchLogs',
                params: { id: item.id }
              }"
              v-bind="attrs"
              v-on="on"
              class="mx-1"
            >
              <v-icon small>fa-history</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("viewOperations") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              @click.stop="openStatus(item.id, item.status)"
              v-bind="attrs"
              v-on="on"
              class="mx-1"
            >
              <v-icon small>far fa-hourglass</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("changeStatus") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="error"
              @click.stop="confirmDelete(item.id)"
              :disabled="item.status == 3 || item.status == 4"
              v-bind="attrs"
              v-on="on"
              v-if="showDelete"
              class="mx-1"
            >
              <v-icon small>fa-trash</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.translator("delete") }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog
      v-model="deleteDialog"
      max-width="640px"
      persistent
      @keydown.esc="closeDelete"
    >
      <v-card>
        <v-card-title class="headline">{{ dialogTitle }}</v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="closeDelete">{{
            $vuetify.lang.translator("no")
          }}</v-btn>
          <v-btn
            color="error"
            @click="tryDelete"
            :disabled="waitForDelete"
            :loading="waitForDelete"
            >{{ $vuetify.lang.translator("yes") }}</v-btn
          >
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="statusDialog"
      persistent
      max-width="500"
      @keydown.esc="statusDialog = false"
      transition="dialog-top-transition"
    >
      <v-card>
        <v-toolbar dense elevation="2">
          <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="statusDialog = false">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <v-timeline dense>
            <v-timeline-item
              icon="fa-upload"
              fill-dot
              :color="selectedBatchStatus === 1 ? 'green' : 'blue'"
              >{{ $vuetify.lang.translator("uploading") }}
            </v-timeline-item>
            <v-timeline-item
              icon="fa-folder"
              fill-dot
              :color="selectedBatchStatus === 2 ? 'green' : 'blue'"
              >{{ $vuetify.lang.translator("uploadingFinished") }}
            </v-timeline-item>
            <v-timeline-item
              icon="fa-hourglass-start"
              fill-dot
              :color="selectedBatchStatus === 3 ? 'green' : 'blue'"
              >{{
                $vuetify.lang.translator("waitingToStartDiagnosis")
              }}</v-timeline-item
            >
            <v-timeline-item
              icon="fa-hourglass-half"
              fill-dot
              :color="selectedBatchStatus === 4 ? 'green' : 'blue'"
              >{{ $vuetify.lang.translator("diagnosing") }}</v-timeline-item
            >
            <v-timeline-item
              icon="fa-bug"
              fill-dot
              :color="selectedBatchStatus === 5 ? 'green' : 'blue'"
            >
              {{ $vuetify.lang.translator("error") }}
            </v-timeline-item>
            <v-timeline-item
              icon="fa-hourglass-end"
              fill-dot
              :color="selectedBatchStatus === 6 ? 'green' : 'blue'"
              >{{ $vuetify.lang.translator("diagnosingFinished") }}
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            :disabled="
              selectedBatchStatus !== 2 &&
                selectedBatchStatus !== 5 &&
                selectedBatchStatus !== 6
            "
            @click="changeStatus()"
            :loading="waitForStatus"
            block
            >{{ $vuetify.lang.translator("changeStatusToDiagnose") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="helpDialog"
      persistent
      max-width="800"
      @keydown.esc="helpDialog = false"
      transition="dialog-top-transition"
    >
      <v-card>
        <v-toolbar dense elevation="2">
          <v-toolbar-title>{{
            $vuetify.lang.translator("help")
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="helpDialog = false">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <div
            class="mt-6"
            v-html="$vuetify.lang.translator('uploadsHelp')"
          ></div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="success" @click="helpDialog = false">{{
            $vuetify.lang.translator("gotIt")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { handleError } from "@/helpers/helper";

export default {
  name: "Uploads",
  data: () => ({
    breadcrumbsItems: [],
    headers: [],
    items: [],
    loading: true,
    itemsPerPage: 0,
    options: {},
    totalItems: 0,
    showDense: true,
    showDelete: false,
    deleteDialog: false,
    dialogTitle: "",
    waitForDelete: false,
    helpDialog: false,
    showStatus: false,
    statusDialog: false,
    selectedBatch: 0,
    selectedBatchStatus: 0,
    waitForStatus: false
  }),
  watch: {
    options: {
      handler() {
        this.getBatches();
      }
    },
    showDense: function() {
      this.$store.dispatch("saveShowDense", this.showDense ? "show" : "hide");
    }
  },
  created() {
    this.itemsPerPage = parseInt(this.$store.getters.PERPAGE);
    this.showDense = this.$store.getters.SHOWDENSE === "show";

    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("dashboard"),
      to: { name: "Dashboard" }
    });
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("uploadedImages"),
      to: { name: "Uploads" }
    });

    this.headers.push({
      text: this.$vuetify.lang.translator("row"),
      value: "index",
      sortable: false,
      align: "center"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("id"),
      value: "id"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("user"),
      value: "user_name",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("employer"),
      value: "employer_name",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("line"),
      value: "line_name",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("captureDate"),
      value: "captured_at"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("files"),
      value: "files"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("status"),
      value: "status_title",
      sortable: false
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("normal"),
      value: "normal_count",
      sortable: false,
      cellClass: "yellow"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("warning"),
      value: "warning_count",
      sortable: false,
      cellClass: "orange"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("critical"),
      value: "critical_count",
      sortable: false,
      cellClass: "red"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("updateDate"),
      value: "updated_at"
    });
    this.headers.push({
      text: this.$vuetify.lang.translator("operations"),
      value: "actions",
      sortable: false,
      align: "center"
    });

    this.checkPrivileges();
  },
  methods: {
    checkPrivileges() {
      let privileges = this.$store.getters.PRIVILEGES;
      this.showStatus = privileges.indexOf("1") === -1;
      let userType = parseInt(this.$store.getters.USERTYPE);
      let allowed_privileges = [3, 4, 5, 6, 7];
      if (allowed_privileges.indexOf(userType) === -1) {
        this.$router.push({ name: "Error403" });
      }
      allowed_privileges = [3, 4];
      if (allowed_privileges.indexOf(userType) > -1) {
        this.showDelete = true;
      }
    },
    getBatches() {
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      var parameters = null;
      if (sortBy[0]) {
        parameters = {
          page: page,
          size: itemsPerPage,
          sort: (sortDesc[0] ? "-" : "") + sortBy[0]
        };
      } else {
        parameters = {
          page: page,
          size: itemsPerPage
        };
      }
      var self = this;
      this.$http
        .get("/batch/", { params: parameters })
        .then(function(response) {
          self.items = response.data.results;
          self.totalItems = response.data.count;
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.loading = false;
        });
    },
    confirmDelete(id) {
      this.idToBeDeleted = id;
      this.dialogTitle = this.$vuetify.lang.translator("sureToDeleteBatch", id);
      this.deleteDialog = true;
    },
    tryDelete() {
      if (this.idToBeDeleted === 0) {
        this.$toasted.error(this.$vuetify.lang.translator("selectABatch"), {
          duration: 5000,
          keepOnHover: true,
          className:
            self.$store.getters.LANGUAGE == "fa"
              ? "vazir-font-fd"
              : "vazir-font",
          iconPack: "fontawesome",
          icon: "exclamation-triangle",
          position: "top-center"
        });
        return;
      }
      this.waitForDelete = true;
      let self = this;
      self.$http
        .delete("/batch/" + this.idToBeDeleted + "/")
        .then(() => {
          self.$toasted.success(self.$vuetify.lang.translator("batchDeleted"), {
            duration: 5000,
            keepOnHover: true,
            className:
              self.$store.getters.LANGUAGE == "fa"
                ? "vazir-font-fd"
                : "vazir-font",
            iconPack: "fontawesome",
            icon: "check",
            position: "top-center"
          });
          self.getBatches();
          self.deleteDialog = false;
        })
        .catch(error => {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.waitForDelete = false;
        });
    },
    closeDelete() {
      this.idToBeDeleted = 0;
      this.deleteDialog = false;
    },
    openStatus(id, status) {
      this.selectedBatch = id;
      this.selectedBatchStatus = status;
      this.dialogTitle = this.$vuetify.lang.translator("changeBatchStatus", id);
      this.statusDialog = true;
    },
    changeStatus() {
      this.waitForStatus = true;
      var self = this;
      this.$http
        .put("/batch/" + this.selectedBatch + "/status/")
        .then(function() {
          self.$toasted.success(
            self.$vuetify.lang.translator("batchStatusChangeSaved"),
            {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "check",
              position: "top-center"
            }
          );
          self.statusDialog = false;
          self.getBatches();
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.waitForStatus = false;
        });
    },
    updateRecordsPerPage(rowsPerPage) {
      this.$store.dispatch("saveRecordsPerPage", rowsPerPage);
    }
  }
};
</script>
